import type { AsyncThunkAction } from '@reduxjs/toolkit';
import PQueue from 'p-queue';
import { createContext, type PropsWithChildren, use, useCallback, useMemo } from 'react';

import { useCheckOptionMutation } from '@/api/trade-capture';
import { useAppDispatch } from '@/store/hooks.ts';
import { checkOptionThunk, type CheckOptionThunkParams } from '@/store/option/thunks/checkOptionThunk.ts';

const QueueContext = createContext<PQueue>(new PQueue({ concurrency: 1 }));

export function QueueContextProvider({ children }: PropsWithChildren) {
  const queue = useMemo(() => {
    return new PQueue({ concurrency: 1 });
  }, []);
  return <QueueContext value={queue}>{children}</QueueContext>;
}

export function useCheckOption(): [
  (param: CheckOptionThunkParams) => void,
  ReturnType<typeof useCheckOptionMutation>[1],
] {
  const queue = use(QueueContext);
  const dispatch = useAppDispatch();
  const [_checkOption, result] = useCheckOptionMutation();

  const queuedCheckOption = useCallback(
    (params: CheckOptionThunkParams) => {
      return queue.add(() => dispatch(checkOptionThunk(params)));
    },
    [queue],
  );

  return [queuedCheckOption, result];
}

export function useQueuedDispatch() {
  const queue = use(QueueContext);
  const dispatch = useAppDispatch();

  // oxlint-disable-next-line no-explicit-any -- must be any
  return (action: AsyncThunkAction<unknown, unknown, any>) => queue.add(() => dispatch(action));
}
